/**
 * Create / edit a release or a version — the one form for both levels.
 *
 * Typing the number is the whole interface: two numbers (10.2) open a
 * release, three or four (10.2.0.27) make a version, and portal-api files it
 * under its release. "New version" inside a release pre-fills the release's
 * number and pins `parent_release_id`, so a typo cannot land it elsewhere.
 *
 * Presentational: the state and the save live in `useReleaseCuration`, so the
 * folders page and the release page open the same dialog.
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import type { JSX } from "react";

import { statusLabel } from "./release-status.js";
import { RELEASE_STATUSES, type ReleaseDTO, type ReleaseInput } from "./releases-api.js";

export interface ProductOption {
  id: number;
  name: string;
  code?: string | null;
}

interface Props {
  open: boolean;
  editing: ReleaseDTO | null;
  form: ReleaseInput;
  onFormChange: (next: ReleaseInput) => void;
  productId: number | "";
  onProductChange: (id: number | "") => void;
  products: ProductOption[];
  error: string | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

export function ReleaseFormDialog({
  open,
  editing,
  form,
  onFormChange,
  productId,
  onProductChange,
  products,
  error,
  pending,
  onClose,
  onSubmit,
}: Props): JSX.Element {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {editing
          ? `Edit ${editing.parent_release_id === null ? "release" : "version"} ${editing.version}`
          : form.parent_release_id
            ? "New version"
            : "New release"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>
          {form.parent_release_id
            ? "A version inside this release — 10.2.0.27. It carries its own change log, its own Disk 1 / Disk 2 images and its own documents."
            : "A release is a folder: two numbers (10.2, or 11). The versions inside it (10.2.0.27) are added from the release's own page — type three or four numbers here and it will be filed under its release automatically."}{" "}
          The change log is Markdown and is the version's release notes — it is written here and
          shown on the release page, nowhere else. Link the disk images afterwards from the
          version's Manage files.
        </DialogContentText>
        <Box
          component="form"
          id="release-form"
          sx={{ display: "grid", gap: 1.5, mt: 2 }}
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {/* PRODUCT IS ASKED ONCE, WHEN THE ROW IS CREATED (2026-09-16).
              Editing showed it as a disabled field that could not be changed
              and usually read "#undefined": a release stores `product_name` /
              `product_code`, never a product id, and portal-api's serializer
              emits no `product_id` at all — so the fallback rendered the word
              "undefined" on every release whose name was never set. A control
              that cannot be used and cannot be trusted is worse than no
              control. */}
          {!editing && (
            <TextField
              select
              id="rel-product"
              label="Product *"
              size="small"
              fullWidth
              value={productId === "" ? "" : String(productId)}
              onChange={(e) => onProductChange(e.target.value ? Number(e.target.value) : "")}
            >
              <MenuItem value="">Select a product…</MenuItem>
              {products.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.name}
                  {p.code ? ` (${p.code})` : ""}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            id="rel-version"
            label="Version *"
            size="small"
            fullWidth
            required
            // Label already embeds its "*"; suppress MUI's auto-asterisk so
            // it doesn't render "Version * *".
            InputLabelProps={{ required: false }}
            placeholder={form.parent_release_id ? "e.g. 10.2.0.27" : "e.g. 10.2"}
            value={form.version}
            onChange={(e) => onFormChange({ ...form, version: e.target.value })}
          />
          <TextField
            select
            id="rel-status"
            label="Status"
            size="small"
            fullWidth
            value={form.status}
            onChange={(e) => onFormChange({ ...form, status: e.target.value })}
          >
            {RELEASE_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {statusLabel(s)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id="rel-desc"
            label="Description"
            size="small"
            fullWidth
            value={form.description ?? ""}
            onChange={(e) => onFormChange({ ...form, description: e.target.value })}
          />
          <TextField
            id="rel-notes"
            label="Change log (Markdown — the version's release notes)"
            size="small"
            fullWidth
            multiline
            minRows={6}
            value={form.release_notes ?? ""}
            onChange={(e) => onFormChange({ ...form, release_notes: e.target.value })}
            sx={{ "& .MuiInputBase-input": { fontFamily: "ui-monospace, monospace" } }}
          />
          {error && <Typography sx={{ fontSize: 14, color: "error.main" }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button variant="contained" type="submit" form="release-form" disabled={pending}>
          {editing ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
